import { ref } from 'vue'

import { localize } from '@vee-validate/i18n'
import { defineStore } from 'pinia'

import { getLocale } from '@/lang'
import { eTheme, theme } from '@/theming'
import { AllowedLang, LangNameAttribute } from '@/types/shared'
import {
  getLanguages,
  getSidebarStatus,
  getSize,
  setLanguage as setCookieLanguage,
  setLanguages as setCookieLanguages,
  setSidebarStatus,
  setSize as setCookieSize,
} from '@/utils/cookies'

export enum eDeviceType {
  Mobile,
  Desktop,
}

export const useAppStore = defineStore('app', () => {
  // state
  const sidebar = ref({
    opened: getSidebarStatus() !== 'closed',
    withoutAnimation: false,
  })
  const device = ref(eDeviceType.Desktop)
  const language = ref(getLocale())
  const size = ref(getSize() || 'default')
  const languages = ref(getLanguages() || {})
  const themeValue = ref(theme() || eTheme.FU)
  const maintenanceMode = ref<string | null>(null)

  // actions
  function toggleSideBar(withoutAnimation: boolean) {
    sidebar.value.opened = !sidebar.value.opened
    sidebar.value.withoutAnimation = withoutAnimation
    setSidebarStatus(sidebar.value.opened ? 'opened' : 'closed')
  }

  function closeSideBar(withoutAnimation: boolean) {
    sidebar.value.opened = false
    sidebar.value.withoutAnimation = withoutAnimation
    setSidebarStatus('closed')
  }

  function toggleDevice(deviceType: eDeviceType) {
    device.value = deviceType
  }

  function setLanguage(newLanguage: AllowedLang) {
    language.value = newLanguage
    setCookieLanguage(newLanguage)
    localize(newLanguage)
  }

  function setLanguages(newLanguages: LangNameAttribute) {
    languages.value = newLanguages
    setCookieLanguages(newLanguages)
  }

  function setSize(newSize: string) {
    size.value = newSize
    setCookieSize(newSize)
  }

  function setMaintenanceMode(mode: string | null) {
    maintenanceMode.value = mode
  }

  return {
    // state
    sidebar,
    device,
    language,
    size,
    languages,
    theme: themeValue,
    maintenanceMode,

    // actions
    toggleSideBar,
    closeSideBar,
    toggleDevice,
    setLanguage,
    setLanguages,
    setSize,
    setMaintenanceMode,
  }
})
